<template>
  <div class="login-page">
    <div class="bg-carousel">
      <transition name="bg-fade">
        <div
          class="bg-slide"
          :key="currentSlide"
          :style="{ backgroundImage: 'url(' + slides[currentSlide].url + ')' }"
        />
      </transition>
      <div class="bg-overlay" />
    </div>

    <div class="login-header">
      <span class="header-brand">CIKON 沁尔康</span>
      <span class="header-slogan">沁尔康，20年只做净水一件事！</span>
    </div>

    <div class="login-body">
      <div class="hero-text">
        <transition name="text-fade" mode="out-in">
          <div :key="currentSlide" class="hero-inner">
            <h1 class="hero-title">{{ slides[currentSlide].title }}</h1>
            <p class="hero-sub">{{ slides[currentSlide].sub }}</p>
          </div>
        </transition>
        <div class="hero-dots">
          <span
            v-for="(s, i) in slides"
            :key="i"
            :class="['dot', { active: i === currentSlide }]"
            @click="currentSlide = i"
          />
        </div>
      </div>

      <div class="login-card">
        <h2 class="card-title">沁尔康智饮云管理系统</h2>

        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          :validate-on-rule-change="false"
          label-position="top"
          class="login-form"
          @keyup.enter="handleSubmit"
        >
          <el-form-item label="账号" prop="username">
            <el-input
              v-model="form.username"

              class="login-input"
              placeholder="请输入账号"
              clearable
              :prefix-icon="legacyIcon('el-icon-user')"
            />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input
              v-model="form.password"

              class="login-input"
              :type="pwdVisible ? 'text' : 'password'"
              placeholder="请输入密码"
              :prefix-icon="legacyIcon('el-icon-lock')"
            >
              <template #suffix>
                <el-icon style="cursor:pointer;opacity:.6" @click.stop="pwdVisible = !pwdVisible">
                  <View />
                </el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item label="验证码" prop="captcha">
            <div class="captcha-row">
              <el-input
                v-model="form.captcha"

                class="login-input captcha-input"
                placeholder="请输入验证码"
                maxlength="4"
                :prefix-icon="legacyIcon('el-icon-key')"
              />
              <img
                v-if="captchaImage"
                :src="captchaImage"
                class="captcha-image"
                alt="验证码"
                @click="refreshCaptcha"
              >

            </div>
          </el-form-item>

          <div class="form-extra">
            <el-checkbox v-model="form.remember">记住密码</el-checkbox>
            <el-checkbox v-model="form.autoLogin">自动登录</el-checkbox>
          </div>

          <el-button
            type="primary"
            class="submit-btn"
            :loading="loading"
            @click="handleSubmit"
          >
            登录
          </el-button>
        </el-form>

        <div class="card-footer">
          登录即表示同意&nbsp;
          <a href="javascript:;" class="link">用户协议</a>&nbsp;和&nbsp;
          <a href="javascript:;" class="link">隐私政策</a>
        </div>
      </div>
    </div>

    <div class="login-footer">
      <span>全国服务热线：<strong>400-8288-238</strong></span>
      <span class="sep">|</span>
      <span>江苏沁尔康环境电器有限公司</span>
    </div>

    <!-- BUG-149：初始密码首次登录强制改密 -->
    <el-dialog
      title="首次登录须修改密码"
      v-model="forcePwdVisible"
      width="420px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      class="force-pwd-dialog"
    >
      <p class="force-pwd-tip">为保障账号安全，首次登录请设置新密码（8-20位，须包含字母、数字和符号）。</p>
      <el-form ref="forcePwdForm" :model="forcePwdData" :rules="forcePwdRules" label-width="90px">
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="forcePwdData.newPassword" type="password" show-password maxlength="20" placeholder="8-20位，含字母、数字、符号" />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="forcePwdData.confirmPassword" type="password" show-password maxlength="20" placeholder="再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" :loading="forcePwdSubmitting" style="width:100%;" @click="handleForcePwdSubmit">确认修改并登录</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { getCaptcha, login, refreshToken, changePassword } from '@/api/auth'
import { View } from '@element-plus/icons-vue'

const LS_REMEMBER = 'qek_remember'
const LS_AUTO_LOGIN = 'qek_auto_login'
const LS_REFRESH_TOKEN = 'qek_refresh_token'

function encrypt(text) {
  return btoa(encodeURIComponent(text))
}

function decrypt(text) {
  try { return decodeURIComponent(atob(text)) } catch { return '' }
}

export default {
  name: 'LoginPage',
  components: { View },
  data() {
    return {
      loading: false,
      pwdVisible: false,
      forcePwdVisible: false,
      forcePwdSubmitting: false,
      forcePwdData: { newPassword: '', confirmPassword: '' },
      currentSlide: 0,
      slideTimer: null,
      captchaKey: '',
      captchaImage: '',
      form: {
        username: '',
        password: '',
        captcha: '',
        remember: false,
        autoLogin: false,
      },
      rules: {
        username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
        captcha: [{ required: true, message: '请输入验证码', trigger: 'blur' }],
      },
      forcePwdRules: {
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 8, max: 20, message: '密码长度须为8-20位', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value && (!/[A-Za-z]/.test(value) || !/\d/.test(value) || !/[^A-Za-z0-9]/.test(value))) {
                return callback(new Error('密码须包含字母、数字和符号'))
              }
              callback()
            },
            trigger: 'blur',
          },
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value !== this.forcePwdData.newPassword) {
                return callback(new Error('两次输入的密码不一致'))
              }
              callback()
            },
            trigger: 'blur',
          },
        ],
      },
      slides: [
        {
          title: '沁尔康，20年只做净水一件事',
          sub: '场景化健康饮水方案定制专家，覆盖校园、办公、医疗等多行业。',
          url: require('@/assets/bg-water-1.jpg'),
        },
        {
          title: '智饮云平台 · 数智物联',
          sub: '在线水质监测、滤芯智能提醒、远程运维管理，尽在云端。',
          url: require('@/assets/bg-water-2.jpg'),
        },
        {
          title: '商务净饮新标杆',
          sub: '每间办公室都需要一台沁尔康——融合奢简，极致鲜活。',
          url: require('@/assets/bg-water-3.jpg'),
        },
      ],
    }
  },
  async mounted() {
    this.restoreCredentials()
    // 浏览器自动填充是异步的，延迟清除未勾选记住密码时的浏览器自动填充内容
    if (!this.form.remember) {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.form.remember) {
            this.form.username = ''
            this.form.password = ''
          }
        }, 150)
      })
    }
    const autoLoggedIn = await this.tryAutoLogin()
    if (!autoLoggedIn) {
      this.refreshCaptcha()
    }
    this.slideTimer = setInterval(() => {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length
    }, 5000)
  },
  beforeUnmount() {
    clearInterval(this.slideTimer)
  },
  watch: {
    'form.remember'(val) {
      if (!val) {
        localStorage.removeItem(LS_REMEMBER)
      }
    },
    'form.autoLogin'(val) {
      if (!val) {
        localStorage.removeItem(LS_AUTO_LOGIN)
        localStorage.removeItem(LS_REFRESH_TOKEN)
      }
    },
  },
  methods: {
    async refreshCaptcha() {
      try {
        const res = await getCaptcha()
        if (res.code === 0 && res.data) {
          this.captchaKey = res.data.captchaKey
          this.captchaImage = res.data.captchaImage
        } else {
          this.$message.error('获取验证码失败')
        }
      } catch (e) {
        console.error('获取验证码失败', e)
        this.$message.error('获取验证码失败，请检查后端服务')
      }
    },
    // 登录失败提示归一化：网络/服务端故障给出可操作的中文提示，
    // 避免把 axios 英文技术文案（Request failed with status code 500 / timeout of 30000ms）直出给用户。
    // （2026-09-10 排查：登录中途切网、关代理会让请求在途失败，用户易误判为密码错误而反复重试）
    resolveLoginErrorMsg(e) {
      if (e && e._isNetworkError) {
        return '网络连接异常，请检查网络（如代理、VPN）后重试'
      }
      const msg = e && e.message ? String(e.message) : ''
      if (!msg || /Request failed with status code|timeout of \d+ms|Network Error/i.test(msg)) {
        return '登录失败，请稍后重试'
      }
      return msg
    },
    async handleSubmit() {
      if (this.loading) return
      this.$refs.form.validate(async valid => {
        if (!valid) return
        if (!this.captchaKey) {
          this.$message.error('请先获取验证码')
          return
        }
        this.loading = true
        try {
          const res = await login({
            username: this.form.username,
            password: this.form.password,
            captcha: this.form.captcha,
            captchaKey: this.captchaKey,
          })
          if (res.code === 0) {
            this.$message.success('登录成功')
            localStorage.setItem('token', res.data.token)
            if (res.data.employee) {
              localStorage.setItem('user', JSON.stringify(res.data.employee))
            }
            if (res.data.refreshToken) {
              localStorage.setItem(LS_REFRESH_TOKEN, res.data.refreshToken)
            }
            this.handleRemember()
            // BUG-149：初始密码首次登录强制改密
            if (res.data.employee && res.data.employee.mustChangePassword) {
              this.forcePwdData = { newPassword: '', confirmPassword: '' }
              this.forcePwdVisible = true
            } else {
              this.$router.replace('/main/dashboard')
            }
          }
        } catch (e) {
          this.$message.error(this.resolveLoginErrorMsg(e))
          this.refreshCaptcha()
          this.form.captcha = ''
        } finally {
          this.loading = false
        }
      })
    },
    async handleForcePwdSubmit() {
      if (this.forcePwdSubmitting) return
      this.$refs.forcePwdForm.validate(async valid => {
        if (!valid) return
        this.forcePwdSubmitting = true
        try {
          const res = await changePassword({
            oldPassword: this.form.password,
            newPassword: this.forcePwdData.newPassword,
            confirmPassword: this.forcePwdData.confirmPassword,
          })
          if (res.code === 0) {
            this.$message.success('密码修改成功')
            this.forcePwdVisible = false
            const user = JSON.parse(localStorage.getItem('user') || '{}')
            user.mustChangePassword = false
            localStorage.setItem('user', JSON.stringify(user))
            this.$router.replace('/main/dashboard')
          } else {
            this.$message.error(res.msg || '密码修改失败')
          }
        } catch (e) {
          this.$message.error(e.message || '密码修改失败')
        } finally {
          this.forcePwdSubmitting = false
        }
      })
    },
    handleRemember() {
      if (this.form.remember) {
        localStorage.setItem(LS_REMEMBER, JSON.stringify({
          username: encrypt(this.form.username),
          password: encrypt(this.form.password),
        }))
      } else {
        localStorage.removeItem(LS_REMEMBER)
      }
      if (this.form.autoLogin) {
        localStorage.setItem(LS_AUTO_LOGIN, '1')
      } else {
        localStorage.removeItem(LS_AUTO_LOGIN)
      }
    },
    restoreCredentials() {
      try {
        const saved = JSON.parse(localStorage.getItem(LS_REMEMBER))
        if (saved) {
          this.form.username = decrypt(saved.username)
          this.form.password = decrypt(saved.password)
          this.form.remember = true
        } else {
          localStorage.removeItem(LS_REMEMBER)
        }
        if (localStorage.getItem(LS_AUTO_LOGIN)) {
          this.form.autoLogin = true
        } else {
          localStorage.removeItem(LS_AUTO_LOGIN)
        }
      } catch { /* ignore */ }
    },
    async tryAutoLogin() {
      if (!localStorage.getItem(LS_AUTO_LOGIN)) return false
      const rt = localStorage.getItem(LS_REFRESH_TOKEN)
      if (!rt) return false
      try {
        const res = await refreshToken({ refreshToken: rt })
        if (res.code === 0 && res.data) {
          localStorage.setItem('token', res.data.token)
          this.$router.replace('/main/dashboard')
          return true
        } else {
          localStorage.removeItem(LS_AUTO_LOGIN)
          localStorage.removeItem(LS_REFRESH_TOKEN)
        }
      } catch {
        localStorage.removeItem(LS_AUTO_LOGIN)
        localStorage.removeItem(LS_REFRESH_TOKEN)
      }
      return false
    },
  },
}
</script>

<style scoped>
.login-page {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  color: #1d1d1f;
}

.bg-carousel {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.bg-slide {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-color: #5a7a8a;
  animation: ken-burns 20s ease-in-out infinite alternate;
}

@keyframes ken-burns {
  0% { transform: scale(1); }
  100% { transform: scale(1.05); }
}

.bg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
}

.bg-fade-enter-active,
.bg-fade-leave-active {
  transition: opacity 1.2s ease;
}
.bg-fade-enter,
.bg-fade-leave-to {
  opacity: 0;
}

.login-header {
  position: relative;
  z-index: 2;
  height: 56px;
  padding: 0 48px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
}

.header-brand {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1.5px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.header-slogan {
  margin-left: 24px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.login-body {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 13% 0 12%;
  gap: 56px;
  min-height: 0;
}

.hero-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-inner {
  margin-bottom: 32px;
}

.hero-title {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 14px;
  letter-spacing: -0.5px;
  color: #fff;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.35), 0 1px 3px rgba(0, 0, 0, 0.25);
}

.hero-sub {
  font-size: 16px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.88);
  margin: 0;
  white-space: nowrap;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
}

.text-fade-enter-active,
.text-fade-leave-active {
  transition: all 0.5s ease;
}
.text-fade-enter {
  opacity: 0;
  transform: translateY(10px);
}
.text-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.hero-dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  width: 24px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.92);
}

.login-card {
  width: 380px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(30px) saturate(1.6);
  -webkit-backdrop-filter: blur(30px) saturate(1.6);
  border-radius: 24px;
  padding: 36px 36px 28px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.3) inset;
}

.card-title {
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
}

.login-form :deep(.el-form-item) {
  margin-bottom: 20px;
}

.login-form :deep(.el-form-item__label) {
  font-size: 13px;
  color: #3a3a3c;
  font-weight: 600;
  line-height: 1.4;
  padding-bottom: 8px;
  margin-bottom: 0;
}

.login-form :deep(.el-form-item__label::before) {
  color: #ff3b30 !important;
  margin-right: 2px;
}

.login-form :deep(.el-form-item__error) {
  position: relative;
  padding-top: 6px;
  color: #ff3b30;
  font-size: 12px;
  line-height: 1.4;
}

/* Element Plus 输入框 */
.login-form :deep(.login-input) {
  width: 100%;
}

.login-form :deep(.login-input .el-input__wrapper) {
  min-height: 46px;
  padding: 0 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.04),
    inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.login-form :deep(.login-input .el-input__wrapper:hover) {
  background: #fff;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 0 0 1px rgba(0, 0, 0, 0.1);
}

.login-form :deep(.login-input.is-focus .el-input__wrapper) {
  background: #fff;
  box-shadow:
    0 0 0 3px rgba(0, 122, 255, 0.18),
    inset 0 0 0 1px #007aff;
}

.login-form :deep(.el-form-item.is-error .login-input .el-input__wrapper) {
  box-shadow:
    0 0 0 3px rgba(255, 59, 48, 0.12),
    inset 0 0 0 1px #ff3b30;
}

.login-form :deep(.login-input .el-input__inner) {
  height: 46px;
  line-height: 46px;
  font-size: 15px;
  color: #1d1d1f;
}

.login-form :deep(.login-input .el-input__inner::placeholder) {
  color: #aeaeb2;
  font-size: 14px;
}

.login-form :deep(.login-input .el-input__prefix) {
  color: #8e8e93;
}

.login-form :deep(.login-input .el-input__prefix .el-icon) {
  font-size: 17px;
}

.pwd-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: #8e8e93;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;
}

.pwd-toggle:hover {
  color: #007aff;
  background: rgba(0, 122, 255, 0.08);
}

.captcha-row {
  display: flex;
  gap: 12px;
  align-items: stretch;
  width: 100%;
}

.captcha-input {
  flex: 1;
  min-width: 0;
}

.captcha-btn {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 12px;
  line-height: 0;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.captcha-btn:hover {
  transform: translateY(-1px);
}

.captcha-btn:active {
  transform: translateY(0);
}

.captcha-image {
  flex-shrink: 0;
  border-radius: 12px;
  cursor: pointer;
  height: 44px;
  width: 120px;
  height: 48px;
  border-radius: 12px;
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.04),
    inset 0 0 0 1px rgba(0, 0, 0, 0.08);
  background: #f4f6f9;
}

.captcha-btn:hover .captcha-canvas {
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    inset 0 0 0 1px rgba(0, 122, 255, 0.35);
}

.form-extra {
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 4px 0 22px;
}

.form-extra :deep(.el-checkbox) {
  height: auto;
}

.form-extra :deep(.el-checkbox__label) {
  color: #3a3a3c;
  font-size: 13px;
  padding-left: 8px;
}

.form-extra :deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: #007aff;
}

.form-extra :deep(.el-checkbox__inner) {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border-color: rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.9);
}

.form-extra :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #007aff;
  border-color: #007aff;
}

.submit-btn {
  width: 100%;
  height: 46px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 500;
  background: #007aff;
  border-color: #007aff;
  letter-spacing: 2px;
  transition: all 0.2s ease;
}

.submit-btn:hover,
.submit-btn:focus {
  background: #0066d6;
  border-color: #0066d6;
  box-shadow: 0 4px 16px rgba(0, 122, 255, 0.3);
}

.card-footer {
  margin-top: 18px;
  text-align: center;
  font-size: 12px;
  color: #636366;
}

.link {
  color: #007aff;
  text-decoration: none;
  cursor: pointer;
}

.link:hover {
  text-decoration: underline;
}

.login-footer {
  position: relative;
  z-index: 2;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  flex-shrink: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.login-footer strong {
  color: #fff;
}

.sep {
  margin: 0 12px;
  color: rgba(255, 255, 255, 0.35);
}
</style>
